/**
 * Last-resort recovery for deployment-skew chunk failures.
 *
 * When a page outlives its deployment (tab older than the Vercel skew window,
 * bfcache-restored HTML, pre-skew-protection clients), lazy navigation loads a
 * chunk from a NEWER deployment and the Turbopack runtime throws:
 *
 *   "Failed to load chunk /_next/static/chunks/…"
 *   "Module X was instantiated because it was required from module Y, but the
 *    module factory is not available"
 *
 * Nothing inside the stale page can fix that — the only recovery is a full
 * reload, which fetches fresh HTML referencing a consistent chunk set. This
 * guard turns the user-visible dead click / white section into one automatic
 * reload, and reports the event to Sentry first so the skew volume stays
 * measurable.
 *
 * Loop safety: reloads are rate-limited via sessionStorage — if a reload
 * already happened within the cooldown, the error is left to surface normally
 * (a genuinely broken deployment must not become an infinite refresh loop).
 * Installed once from instrumentation-client.ts (same pattern as
 * domMutationGuard); the Symbol marker keeps double-installs no-ops.
 *
 * Render-phase failures (a lazy component whose module factory is gone) are
 * caught by React error boundaries and never reach the window listeners, so
 * app/error.tsx and app/global-error.tsx call recoverFromChunkLoadError()
 * directly to get the same one-shot reload.
 */
import * as Sentry from '@sentry/nextjs';

const guardMarker = Symbol.for('universalx.chunkLoadErrorGuard');
const RELOAD_TS_STORAGE_KEY = '__ux_chunk_error_reloaded_at__';
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;
// Give Sentry a beat to flush the event; never hold the recovery hostage to it.
const SENTRY_FLUSH_TIMEOUT_MS = 1500;

const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /failed to load chunk/i, // Turbopack runtime chunk fetch failure
  /module factory is not available/i, // Turbopack cross-deployment module id mismatch
  /ChunkLoadError|loading chunk .* failed|loading css chunk/i, // webpack-style, defensive
  /failed to fetch dynamically imported module|importing a module script failed/i, // native ESM errors
];

export function isChunkLoadErrorMessage(message: unknown): boolean {
  return typeof message === 'string' && CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

const toMessage = (candidate: unknown): string => {
  if (typeof candidate === 'string') return candidate;
  if (candidate instanceof Error) return `${candidate.name}: ${candidate.message}`;
  if (candidate && typeof candidate === 'object') {
    const value = candidate as { name?: unknown; message?: unknown };
    const name = typeof value.name === 'string' ? value.name : '';
    const message = typeof value.message === 'string' ? value.message : '';
    return `${name}: ${message}`;
  }
  return '';
};

export function isChunkLoadError(error: unknown): boolean {
  return isChunkLoadErrorMessage(toMessage(error));
}

interface GuardDeps {
  reload: () => void;
  captureException: (error: unknown, context?: object) => unknown;
  flush: (timeoutMs: number) => Promise<unknown>;
  now: () => number;
}

const defaultDeps: GuardDeps = {
  reload: () => window.location.reload(),
  captureException: (error, context) => Sentry.captureException(error, context),
  flush: (timeoutMs) => Sentry.flush(timeoutMs),
  now: () => Date.now(),
};

export type ChunkLoadRecovery = (error: unknown, source: string) => boolean;

/**
 * Builds the shared "report → cooldown check → reload once" recovery. Returns
 * true when a reload has been scheduled, false when the cooldown says a reload
 * already failed to fix it and the error should surface to the user.
 */
export function createChunkLoadRecovery(win: Window, deps: GuardDeps = defaultDeps): ChunkLoadRecovery {
  const readLastReloadAt = (): number | undefined => {
    try {
      return Number(win.sessionStorage.getItem(RELOAD_TS_STORAGE_KEY)) || 0;
    } catch {
      return undefined;
    }
  };

  let recovering = false;
  return (error, source) => {
    if (recovering) return true;
    const lastReloadAt = readLastReloadAt();
    const withinCooldown = lastReloadAt !== undefined && deps.now() - lastReloadAt < RELOAD_COOLDOWN_MS;
    let canPersistReload = lastReloadAt !== undefined;
    if (!withinCooldown && canPersistReload) {
      try {
        win.sessionStorage.setItem(RELOAD_TS_STORAGE_KEY, String(deps.now()));
      } catch {
        canPersistReload = false;
      }
    }
    const willReload = !withinCooldown && canPersistReload;
    const message = toMessage(error);
    try {
      deps.captureException(error instanceof Error ? error : new Error(`[chunkLoadErrorGuard] ${message}`), {
        tags: { chunkLoadGuard: source, chunkLoadGuardAction: willReload ? 'reloaded' : 'surfaced' },
      });
    } catch {
      // Sentry unavailable — recovery still proceeds
    }
    if (!willReload) return false; // reloading cannot be bounded safely — let the error surface
    recovering = true;
    let reloaded = false;
    const reloadOnce = () => {
      if (reloaded) return;
      reloaded = true;
      deps.reload();
    };
    try {
      void deps.flush(SENTRY_FLUSH_TIMEOUT_MS).then(reloadOnce, reloadOnce);
    } catch {
      // flush threw synchronously — fall through to the timer below
    }
    // Hard deadline in case flush never settles.
    win.setTimeout(reloadOnce, SENTRY_FLUSH_TIMEOUT_MS + 500);
    return true;
  };
}

// Single page-level recovery shared by the window listeners and the React
// error boundaries, so both paths share one in-flight flag.
let pageRecovery: ChunkLoadRecovery | undefined;

/**
 * Entry point for React error boundaries (app/error.tsx, app/global-error.tsx).
 *
 * Errors thrown while rendering — e.g. a lazily loaded component whose module
 * factory is missing after a deployment — are caught by React and never reach
 * the window listeners below, so the boundary has to opt in explicitly. Returns
 * true when a reload has been scheduled (render a neutral "reloading" state),
 * false when the error should surface (dev, SSR, or within the cooldown).
 */
export function recoverFromChunkLoadError(error: unknown, source: string): boolean {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') return false;
  pageRecovery ??= createChunkLoadRecovery(window);
  return pageRecovery(error, source);
}

/** Returns an uninstall function (used by tests; production installs once for the page's lifetime). */
export function installChunkLoadErrorGuard(win: Window, deps: GuardDeps = defaultDeps): () => void {
  const holder = win as Window & { [guardMarker]?: boolean };
  if (holder[guardMarker]) return () => {};
  holder[guardMarker] = true;

  const recover = createChunkLoadRecovery(win, deps);
  const sharesPageRecovery = typeof window !== 'undefined' && win === window && deps === defaultDeps;
  if (sharesPageRecovery) pageRecovery = recover;

  // Dynamic imports reject → unhandledrejection carries the runtime's Error.
  const onRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      const willReload = recover(event.reason, 'unhandledrejection');
      if (willReload) event.preventDefault(); // the reload IS the handler — keep consoles clean
    }
  };

  // Some chunk failures surface as synchronous window errors instead.
  // Capture phase so resource (script/link) load errors are seen too.
  const onError = (event: ErrorEvent) => {
    const error = event.error || event.message;
    if (isChunkLoadError(error)) {
      recover(error, 'window.error');
      return;
    }
    // <script>/<link> element load failures have no message — match by URL.
    const target = event.target as { tagName?: string; src?: string; href?: string } | null;
    const url = target?.src ?? target?.href ?? '';
    if ((target?.tagName === 'SCRIPT' || target?.tagName === 'LINK') && url.includes('/_next/static/')) {
      recover(`static asset failed to load: ${url}`, 'resource');
    }
  };

  win.addEventListener('unhandledrejection', onRejection);
  win.addEventListener('error', onError, true);
  return () => {
    win.removeEventListener('unhandledrejection', onRejection);
    win.removeEventListener('error', onError, true);
    if (sharesPageRecovery) pageRecovery = undefined;
    delete holder[guardMarker];
  };
}

// Recovery-by-reload only makes sense against real deployments; in dev it
// would fight HMR's own chunk churn.
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
  installChunkLoadErrorGuard(window);
}
